/**
 * Parent-side surface for under-13 ghost players linked to this
 * parent via `roster_entries.parent_user_id`.
 *
 * Read-only — the coach manages the roster (`/players` on web2,
 * `/(coach)/roster` on mobile). The parent here is informed,
 * not in control. Sits on /parent (parent home) below the active
 * 13+ child cards.
 *
 * @see docs/spec/22a-v1-launch-posture.md
 */
import { useMemo } from 'react';
import { Card, GlassPill, Radar, Skeleton } from '../primitives';
import { useParentRosterEntries } from '../../lib/queries/parent/useParentRosterEntries';
import {
  useRosterEntryRatings,
  type CornerComposite,
} from '../../lib/queries/parent/useRosterEntryRatings';
import { rosterEntryAge, type RosterEntry } from '@4cm/core/rosterEntry';
import { CORNER_IDS, type CornerId } from '@4cm/core/rosterEntry/rating';
import { CORNER_LABELS } from '@4cm/core/constants';
import { cornerCssVar } from '../../lib/cornerColors';
import './ParentRosterEntries.css';

export function ParentRosterEntries() {
  const { data, loading, error } = useParentRosterEntries();
  const rateeIds = useMemo(() => data.map((d) => d.id), [data]);
  const { data: ratingsByRatee } = useRosterEntryRatings(rateeIds);

  // Hide the entire section when there's nothing to show — parents
  // who only have 13+ kids shouldn't see an empty "Under-13 roster"
  // header for no reason.
  if (loading) {
    return (
      <Card variant="opaque" className="parent-roster">
        <header className="parent-roster__head">
          <span className="parent-page__eyebrow">🧒 Linked under-13 players</span>
        </header>
        <ul className="parent-roster__list" aria-label="Loading">
          {Array.from({ length: 2 }, (_, i) => (
            <li key={i} className="parent-roster__row">
              <Skeleton variant="text" width="40%" height={16} />
              <div style={{ height: 4 }} />
              <Skeleton variant="text" width="25%" height={12} />
            </li>
          ))}
        </ul>
      </Card>
    );
  }

  if (error) {
    return (
      <Card variant="opaque" className="parent-roster">
        <header className="parent-roster__head">
          <span className="parent-page__eyebrow">🧒 Linked under-13 players</span>
        </header>
        <p className="parent-roster__error">
          Couldn't load your roster links. ({error})
        </p>
      </Card>
    );
  }

  if (data.length === 0) return null;

  return (
    <Card variant="opaque" className="parent-roster">
      <header className="parent-roster__head">
        <span className="parent-page__eyebrow">🧒 Linked under-13 players</span>
        <p className="parent-roster__sub">
          Your child's coach added them to their squad. Players under 13 don't
          have their own 4CM login yet — until they do, what you see here is
          what their coach has on record.
        </p>
      </header>
      <ul className="parent-roster__list">
        {data.map((entry) => (
          <RosterEntryRow
            key={entry.id}
            entry={entry}
            composites={ratingsByRatee.get(entry.id) ?? null}
          />
        ))}
      </ul>
    </Card>
  );
}

function RosterEntryRow({
  entry,
  composites,
}: {
  entry: RosterEntry & { team_name: string | null; team_age_group: string | null };
  composites: CornerComposite[] | null;
}) {
  const age = rosterEntryAge(entry);
  return (
    <li className="parent-roster__row">
      <div className="parent-roster__main">
        <div className="parent-roster__name-line">
          {entry.jersey_number != null && (
            <span className="parent-roster__num">#{entry.jersey_number}</span>
          )}
          <span className="parent-roster__name">
            {entry.first_name} {entry.last_name}
          </span>
          {age != null && <GlassPill mono>U{age + 1}</GlassPill>}
        </div>
        <div className="parent-roster__meta">
          {entry.team_name && <span>{entry.team_name}</span>}
          {entry.team_age_group && entry.team_name && <span aria-hidden="true"> · </span>}
          {entry.team_age_group && <span>{entry.team_age_group}</span>}
          {entry.position && (
            <>
              {(entry.team_name || entry.team_age_group) && <span aria-hidden="true"> · </span>}
              <span className="parent-roster__pos">{entry.position}</span>
            </>
          )}
        </div>
        {entry.notes && <p className="parent-roster__notes">{entry.notes}</p>}
        <CornerSummary composites={composites} />
      </div>
    </li>
  );
}

/**
 * Per-row rating summary. Two flavours:
 *
 *   - Untouched roster entry (zero ratings) → short copy explaining
 *     the coach hasn't posted yet.
 *   - Has at least one rating → a small radar (shape) + four corner
 *     tiles (exact numbers). The radar communicates balance; the
 *     tiles communicate precision. Both useful, both aria-labelled.
 */
function CornerSummary({ composites }: { composites: CornerComposite[] | null }) {
  if (!composites) return null;
  const total = composites.reduce((sum, c) => sum + c.count, 0);
  if (total === 0) {
    return (
      <div className="parent-roster__no-ratings">
        Coach hasn&apos;t posted any ratings yet.
      </div>
    );
  }

  // Build {tech,phys,psych,social} for the Radar primitive. Missing
  // corners render as 0 (a deflated radar shape) rather than blanking
  // the chart entirely — visually the kid still "fits" the four-corner
  // model, the coach just hasn't measured everything yet.
  const radarValues = {
    tech: meanFor(composites, 'corner-technical'),
    phys: meanFor(composites, 'corner-physical'),
    psych: meanFor(composites, 'corner-psych'),
    social: meanFor(composites, 'corner-social'),
  };

  return (
    <div className="parent-roster__summary">
      <Radar
        values={radarValues}
        size={120}
        className="parent-roster__radar"
      />
      <div className="parent-roster__tiles" role="group" aria-label="Corner score averages">
        {CORNER_IDS.map((c) => {
          const comp = composites.find((x) => x.corner_id === c);
          const mean = comp?.mean ?? null;
          return (
            <div
              key={c}
              className="parent-roster__tile"
              style={{ borderTopColor: cornerCssVar(c) }}
              aria-label={`${CORNER_LABELS[c]} average`}
            >
              <span className="parent-roster__tile-label" style={{ color: cornerCssVar(c) }}>
                {tileLabelForCorner(c)}
              </span>
              <span className="parent-roster__tile-value">
                {mean == null ? '—' : Math.round(mean)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function meanFor(composites: CornerComposite[], cornerId: CornerId): number {
  const c = composites.find((x) => x.corner_id === cornerId);
  return Math.round(c?.mean ?? 0);
}

function tileLabelForCorner(c: CornerId): string {
  switch (c) {
    case 'corner-technical': return 'TECH';
    case 'corner-physical':  return 'PHYS';
    case 'corner-psych':     return 'PSYCH';
    case 'corner-social':    return 'SOCIAL';
  }
}
